import { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Button } from "components/DesignSystem/Button/Button";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { MenuDotsVertical } from "components/icons/MenuDotsVertical";
import { TaskFiledExternally } from "components/Task/EntityTaskList/TaskFiledExternally";
import { CART_ITEM_STATUS } from "constants/cart";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { emptyApi } from "store/apis/emptyApi";
import { useUpdateCartItemMutation } from "store/apis/products";
import { FilingNode } from "v2/types/filingsDashboard.types";
import { toTaskDeadline } from "v2/utils/toTaskDeadline";

const DETECTED_FILINGS_TAG = "TAX_DETECTED_FILINGS";

type FilingActionsMenuProps = {
  filing: FilingNode;
  entityId: string;
  season: string;
};

const SAVE_FOR_LATER_SUCCESS = "Item has been moved to Save for later";
const MOVE_TO_CART_SUCCESS = "Item has been moved to cart";
const GENERIC_ERROR = "Something went wrong";

export const FilingActionsMenu = ({
  filing,
  entityId,
  season,
}: FilingActionsMenuProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { secondary_channel_url: generalHelpChannel } = useCurrentGroup();
  const { successToast, alertToast } = useToast();

  const filedExternally = useModal();

  const [isOpen, setIsOpen] = useState(false);

  const [updateCartItem, { isLoading: isSaving }] = useUpdateCartItemMutation();

  const taskDeadline = toTaskDeadline(filing, entityId, season);

  const refetchDetectedFilings = (): void => {
    dispatch(emptyApi.util.invalidateTags([DETECTED_FILINGS_TAG]));
  };

  const handleFiledExternallySuccess = (): void => {
    refetchDetectedFilings();
    filedExternally.close();
  };

  const isSavedForLater = filing.status === CART_ITEM_STATUS.SAVED_FOR_LATER;

  const changeCartStatus = async (
    status: string,
    successMessage: string
  ): Promise<void> => {
    if (!filing.cartItemId) return;

    try {
      await updateCartItem({
        entityId,
        payload: {
          cartItemId: filing.cartItemId,
          status: status as "SAVED_FOR_LATER",
        },
      }).unwrap();

      successToast({ message: successMessage });
      refetchDetectedFilings();
      setIsOpen(false);
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        GENERIC_ERROR;
      alertToast({ message });
    }
  };

  const handleToggleCartStatus = (event: Event): void => {
    event.preventDefault();
    if (isSavedForLater) {
      void changeCartStatus(CART_ITEM_STATUS.IN_CART, MOVE_TO_CART_SUCCESS);
      return;
    }
    void changeCartStatus(
      CART_ITEM_STATUS.SAVED_FOR_LATER,
      SAVE_FOR_LATER_SUCCESS
    );
  };

  const handleEnquire = (): void => {
    history.push(`/chat/${generalHelpChannel ?? ""}`);
  };

  return (
    <>
      <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dropdown.Trigger asChild>
          <div>
            <Button
              customType="ghost_icon"
              size="small"
              className="!t-h-4 !t-min-h-0 !t-w-4 !t-px-0"
            >
              <span className="t-flex t-text-neutral">
                <MenuDotsVertical />
              </span>
            </Button>
          </div>
        </Dropdown.Trigger>

        <Dropdown.Portal>
          <Dropdown.Content
            sideOffset={8}
            side="bottom"
            align="end"
            className="t-min-w-52"
          >
            <Dropdown.Item
              onSelect={handleToggleCartStatus}
              disabled={isSaving}
              tag={isSaving ? <Spinner size="small" /> : undefined}
            >
              {isSavedForLater ? "Move to cart" : "Save for later"}
            </Dropdown.Item>

            <Dropdown.Item onSelect={filedExternally.open}>
              Mark filed externally
            </Dropdown.Item>

            <Dropdown.Item onSelect={handleEnquire}>
              Enquire about this filing
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      <TaskFiledExternally
        isOpen={filedExternally.isOpen}
        close={filedExternally.close}
        taskDeadline={taskDeadline}
        onSuccess={handleFiledExternallySuccess}
      />
    </>
  );
};
